import { isEmpty } from "ramda";

import { getCsrfToken } from "./csrf";
import { ReactError } from "./errors";
import Sentry from "./sentry.js";
import { camelCaseKeys, snakeCaseKeys } from "./transform-object-keys.js";

// This type probably won't be sufficient for all use cases, but
// it works for the initial components we have converted to TS.
type DataType = Record<string, unknown>;

class FetchError extends ReactError {
  constructor(...params: ConstructorParameters<typeof ReactError>) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }

    this.name = "FetchError";
  }
}

const handleJsonResponse: <T>(request: Promise<Response>) => Promise<T> = (
  request,
) =>
  request
    .then((response) => {
      if (response.ok || response.status === 422) return response;

      const scope = Sentry.getCurrentScope();
      scope.setExtra("redirected", response.redirected);
      scope.setExtra("url", response.url);

      return Promise.reject(
        new Error(`${response.status} ${response.statusText}`.trim()),
      );
    })
    .then((response) => response.json())
    .then(camelCaseKeys)
    .catch((e: unknown) => {
      if (e instanceof Error && e.name === "AbortError") {
        // Never fulfill the promise, so no `catch` or `then` are called
        return new Promise(() => {});
      }

      const message = e instanceof Error ? e.message : String(e);
      throw new FetchError(`Fetch failed: ${message}`, { cause: e });
    });

const withDefaults = (options: RequestInit): RequestInit => ({
  headers: {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json",
    "X-CSRF-Token": getCsrfToken(),
    "X-Requested-With": "XMLHttpRequest",
  },
  cache: "no-store",
  credentials: "same-origin",
  redirect: "follow",
  ...options,
});

export const postJson = <T>(
  url: string,
  data: DataType = {},
  abortSignal?: AbortSignal,
) =>
  handleJsonResponse<T>(
    fetch(
      url,
      withDefaults({
        method: "POST",
        body: JSON.stringify(snakeCaseKeys(data)),
        signal: abortSignal,
      }),
    ),
  );

export const putJson = <T>(
  url: string,
  data: DataType = {},
  abortSignal?: AbortSignal,
) =>
  handleJsonResponse<T>(
    fetch(
      url,
      withDefaults({
        method: "PUT",
        body: JSON.stringify(snakeCaseKeys(data)),
        signal: abortSignal,
      }),
    ),
  );

export const patchJson = <T>(
  url: string,
  data: DataType = {},
  abortSignal?: AbortSignal,
) =>
  handleJsonResponse<T>(
    fetch(
      url,
      withDefaults({
        method: "PATCH",
        body: JSON.stringify(snakeCaseKeys(data)),
        signal: abortSignal,
      }),
    ),
  );

export const deleteJson = <T>(
  url: string,
  data: DataType = {},
  abortSignal?: AbortSignal,
) =>
  handleJsonResponse<T>(
    fetch(
      url,
      withDefaults({
        method: "DELETE",
        body: JSON.stringify(snakeCaseKeys(data)),
        signal: abortSignal,
      }),
    ),
  );

export const fetchJson = <T>(
  url: string,
  data: DataType = {},
  abortSignal?: AbortSignal,
) => {
  let urlWithParams = url;

  if (!isEmpty(data)) {
    const params = snakeCaseKeys(data) as Record<string, string>;
    urlWithParams = `${url}?${new URLSearchParams(params).toString()}`;
  }

  return handleJsonResponse<T>(
    fetch(urlWithParams, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      signal: abortSignal,
    }),
  );
};

export const postFormAsJson = (
  url: string,
  formData: FormData,
  formName: string,
  abortSignal?: AbortSignal,
) => {
  const payload: Record<string, unknown> = {};

  formData.forEach((value, key) => {
    payload[key] = value;
  });

  return postJson(url, { [formName]: payload }, abortSignal);
};

export const sendBeacon = (url: string, data: DataType = {}) =>
  fetch(
    url,
    withDefaults({
      method: "POST",
      body: JSON.stringify(snakeCaseKeys(data)),
      keepalive: true,
    }),
  );
